import { Field } from '../ui/Field';
import { NumberInput } from '../ui/NumberInput';
import { ProvisionalMark } from '../ui/ProvisionalMark';
import { inputCls } from '../ui/styles';
import { today } from '../../core/format/dates';
import { LASTS_WEEKS_MAX, LASTS_WEEKS_MIN, lastsWeeksSources, usageLabel, usageStatus } from '../../core/rules/usage';
import type { LastsWeeksSource, Product } from '../../core/types/catalog';
type Props = {
  product: Product;
  set: (patch: Partial<Product>) => void;
};
const NOTE_MAX = 140;

/**
 * Gebruiksduur in het producteditor: één getal in weken, met daarnaast hoe betrouwbaar het is.
 * Een oude tekst ("± 3 maanden") blijft gelden tot er een getal staat; dan rekent alles met het getal.
 * Een wijziging geldt voor dozen die nog berekend worden, nooit voor betaalde of verzonden leveringen.
 */
export function UsageFields({
  product,
  set
}: Props) {
  const status = usageStatus(product);
  const shopLabel = usageLabel(product);
  const source = product.lastsWeeksSource ?? 'schatting';
  const sourceHint = lastsWeeksSources.find(s => s.value === source)?.hint;
  return <>
      <Field label="Gaat mee (weken)" hint={status.legacy ? `Nu uit de oude tekst “${product.usesPerBottle}” (± ${status.weeks} weken). Vul een getal in om die te vervangen.` : shopLabel ? `In de winkel: “gaat ${shopLabel} mee”. Eén verpakking bij normaal gebruik.` : 'Eén verpakking bij normaal gebruik. Leeg = gaat bij bijvullen in elke doos mee.'} data-id="element-1032" data-mp="10g8b58thqdal">
        <span className="flex items-center gap-2" data-id="element-1033" data-mp="hinhlofs6qjn">
          <NumberInput value={product.lastsWeeks} onChange={v => set({
          lastsWeeks: v,
          ...(v !== undefined && !product.lastsWeeksSource ? {
            lastsWeeksSource: 'schatting' as const
          } : {})
        })} min={LASTS_WEEKS_MIN} max={LASTS_WEEKS_MAX} allowEmpty placeholder={status.legacy ? String(status.weeks) : 'bijv. 8'} data-id="element-1034" data-mp="wemqh1apsupu" />
          {status.provisional && <ProvisionalMark data-id="element-1035" data-mp="29wc2chdn8iof" />}
        </span>
      </Field>

      <Field label="Betrouwbaarheid" hint={sourceHint} data-id="element-1036" data-mp="2ewgha2ywl5cj">
        <select value={source} onChange={e => set({
        lastsWeeksSource: e.target.value as LastsWeeksSource
      })} disabled={product.lastsWeeks === undefined} className={inputCls} data-id="element-1037" data-mp="2euvn4egi3of7">
          {lastsWeeksSources.map(s => <option key={s.value} value={s.value} data-id="element-1038" data-mp="1r7nkk4agitgv">
              {s.label}
            </option>)}
        </select>
      </Field>

      <Field label="Nagekeken op" hint="Optioneel. Wanneer je de gebruiksduur voor het laatst hebt gecontroleerd." data-id="element-1039" data-mp="1w68lk5xj9h6d">
        <span className="flex items-center gap-2" data-id="element-1040" data-mp="2137ckflwh80c">
          <input type="date" value={product.lastsWeeksCheckedOn ?? ''} max={today()} onChange={e => set({
          lastsWeeksCheckedOn: e.target.value || undefined
        })} className={inputCls} data-id="element-1041" data-mp="25wtqsc7ppjy9" />
          <button type="button" onClick={() => set({
          lastsWeeksCheckedOn: today()
        })} className="shrink-0 text-[13px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-1042" data-mp="1dd4r4ichr0ap">
            Vandaag
          </button>
        </span>
      </Field>

      <Field label="Toelichting" hint="Optioneel, alleen voor het beheer. Waar de waarde op rust." count={{
      value: product.lastsWeeksNote?.length ?? 0,
      max: NOTE_MAX
    }} data-id="element-1043" data-mp="3hnzsxgpocj2w">
        <input value={product.lastsWeeksNote ?? ''} maxLength={NOTE_MAX} onChange={e => set({
        lastsWeeksNote: e.target.value || undefined
      })} className={inputCls} placeholder="Bijv. 2 pompjes per avond, 30 ml" data-id="element-1044" data-mp="37qmrxjg5b889" />
      </Field>
    </>;
}